import React from 'react';
import { Handle, Position } from 'reactflow';
import { useTheme } from '../../contexts/ThemeContext';
import { MessageSquare, Menu, Clock, CheckCircle } from 'lucide-react';

export const NodeMensagem = ({ data, selected }) => {
  const { darkMode } = useTheme();
  return (
    <div className={`px-3 py-2 shadow-md rounded-lg border-2 transition-all relative ${
      selected 
        ? 'border-blue-600 shadow-xl' 
        : darkMode ? 'bg-zinc-800 border-blue-500' : 'bg-white border-blue-400'
    } min-w-[160px] max-w-[200px]`}>
      {data.ordem && (
        <div className="absolute -top-2 -left-2 bg-blue-500 text-white text-xs w-5 h-5 rounded-full font-bold shadow-lg flex items-center justify-center">
          {data.ordem}
        </div>
      )}
      {data.isInicial && (
        <div className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full font-bold shadow-lg">
          INÍCIO
        </div>
      )}
      <Handle type="target" position={Position.Top} className="w-2 h-2 !bg-blue-500" />
      <div className="flex items-center space-x-1.5 mb-1">
        <MessageSquare className="w-4 h-4 text-blue-500" />
        <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {data.label}
        </div>
      </div>
      {data.mensagem && (
        <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'} line-clamp-2`}>
          {data.mensagem.substring(0, 50)}...
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="w-2 h-2 !bg-blue-500" />
    </div>
  );
};

export const NodeMenu = ({ data, selected }) => {
  const { darkMode } = useTheme();
  return (
    <div className={`px-3 py-2 shadow-md rounded-lg border-2 transition-all relative ${
      selected 
        ? 'border-green-600 shadow-xl' 
        : darkMode ? 'bg-zinc-800 border-green-500' : 'bg-white border-green-400'
    } min-w-[160px] max-w-[200px]`}>
      {data.ordem && (
        <div className="absolute -top-2 -left-2 bg-green-500 text-white text-xs w-5 h-5 rounded-full font-bold shadow-lg flex items-center justify-center">
          {data.ordem}
        </div>
      )}
      {data.isInicial && (
        <div className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full font-bold shadow-lg">
          INÍCIO
        </div>
      )}
      <Handle type="target" position={Position.Top} className="w-2 h-2 !bg-green-500" />
      <div className="flex items-center space-x-1.5 mb-1">
        <Menu className="w-4 h-4 text-green-500" />
        <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {data.label}
        </div>
      </div>
      {data.opcoes && data.opcoes.length > 0 && (
        <div className={`text-xs mt-1 space-y-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {data.opcoes.slice(0, 2).map((op, i) => (
            <div key={i}>• {op.opcao}: {op.texto}</div>
          ))}
          {data.opcoes.length > 2 && <div>+ {data.opcoes.length - 2} mais...</div>}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="w-2 h-2 !bg-green-500" />
    </div>
  );
};

export const NodeAguardar = ({ data, selected }) => {
  const { darkMode } = useTheme();
  return (
    <div className={`px-3 py-2 shadow-md rounded-lg border-2 transition-all relative ${
      selected 
        ? 'border-orange-600 shadow-xl' 
        : darkMode ? 'bg-zinc-800 border-orange-500' : 'bg-white border-orange-400'
    } min-w-[160px] max-w-[200px]`}>
      {data.ordem && (
        <div className="absolute -top-2 -left-2 bg-orange-500 text-white text-xs w-5 h-5 rounded-full font-bold shadow-lg flex items-center justify-center">
          {data.ordem}
        </div>
      )}
      <Handle type="target" position={Position.Top} className="w-2 h-2 !bg-orange-500" />
      <div className="flex items-center space-x-1.5 mb-1">
        <Clock className="w-4 h-4 text-orange-500" />
        <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {data.label}
        </div>
      </div>
      <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {data.aguardar_modo === 'tempo'
          ? `⏱️ Espera ${data.aguardar_minutos || '?'} min`
          : '⏳ Aguarda resposta'}
      </div>
      <Handle type="source" position={Position.Bottom} className="w-2 h-2 !bg-orange-500" />
    </div>
  );
};

export const NodeFinalizar = ({ data, selected }) => {
  const { darkMode } = useTheme();
  return (
    <div className={`px-3 py-2 shadow-md rounded-lg border-2 transition-all relative ${
      selected 
        ? 'border-red-600 shadow-xl' 
        : darkMode ? 'bg-zinc-800 border-red-500' : 'bg-white border-red-400'
    } min-w-[160px] max-w-[200px]`}>
      {data.ordem && (
        <div className="absolute -top-2 -left-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full font-bold shadow-lg flex items-center justify-center">
          {data.ordem}
        </div>
      )}
      <Handle type="target" position={Position.Top} className="w-2 h-2 !bg-red-500" />
      <div className="flex items-center space-x-1.5 mb-1">
        <CheckCircle className="w-4 h-4 text-red-500" />
        <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {data.label}
        </div>
      </div>
      <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        ✅ Encerrar
      </div>
    </div>
  );
};

export const nodeTypes = {
  mensagem: NodeMensagem,
  menu: NodeMenu,
  aguardar: NodeAguardar,
  finalizar: NodeFinalizar,
};
